import {
  entries,
  get,
  isArray,
  isPlainObject,
  isString,
  keys,
  template,
  values,
  zipObject,
} from 'lodash-es';

import config from 'configAlias';
import defaultProperties from 'configFolderAlias/analytics/default.json';
export const extendKeyPath = (keyPath, segment) => {
  const getUniqueName = (name, increment = 2) => {
    if (!(name in keyPath)) {
      return name;
    }

    if (!(name + increment in keyPath)) {
      return name + increment;
    }
    return getUniqueName(name, increment + 1);
  };

  const uniqueNames = keys(segment).map((name) => getUniqueName(name));
  return { ...keyPath, ...zipObject(uniqueNames, values(segment)) };
};

export const transformKeyPath = (keyPath) => {
  if (isPlainObject(keyPath)) {
    return keyPath;
  }

  return (function recurse(path) {
    if (isString(path)) {
      return recurse(path.split('.'));
    }

    return path.reduce((acc, value, index) => {
      const key = !index
        ? 'pageName'
        : /^[0-9]+$/.test(value) //NOSONAR
        ? 'index'
        : 'propertyName';

      return extendKeyPath(acc, { [key]: value });
    }, {});
  })(keyPath);
};

export const removeIndexFromKeyPath = (keyPath) => {
  if (!isPlainObject(keyPath)) {
    throw new Error('keyPath must be a plain object.');
  }

  return entries(keyPath).reduce((acc, [key, value]) => {
    if (/^index(?:\d+)?$/.test(key) || !value) {
      return acc;
    }
    return [...acc, value];
  }, []);
};

export const resolvePatternCondition = (action, formValues, condition = {}) => {
  //NOSONAR
  if (condition.pattern) {
    const regex = new RegExp(condition.pattern);

    return regex.test(formValues);
  }

  return true;
};

export const resolveEqualityCondition = (
  condition = {}, //NOSONAR
  action,
  formValues
) => {
  if (condition.equality) {
    return (function recurse(eqCondition) {
      //NOSONAR
      if (eqCondition.anyOf && isArray(eqCondition.anyOf)) {
        return eqCondition.anyOf.some((item) => recurse(item) === true);
      }

      if (isPlainObject(eqCondition)) {
        return entries(eqCondition).every(
          ([keyPath, value]) =>
            get(
              {
                action,
                formValues,
              },
              keyPath,
              null
            ) === value
        );
      }

      return true;
    })(condition.equality);
  }

  return true;
};

export const resolvePresenceCondition = (
  condition = {}, //NOSONAR
  action,
  formValues
) => {
  if (condition.presence) {
    return (function recurse(presCondition) {
      //NOSONAR
      if (presCondition.anyOf && isArray(presCondition.anyOf)) {
        return presCondition.anyOf.some((item) => recurse(item) === true);
      }

      if (isArray(presCondition)) {
        return presCondition.every(
          (keyPath) =>
            typeof get({ action, formValues }, keyPath, null) !== 'undefined'
        );
      }

      return true;
    })(condition.presence);
  }

  return true;
};

export const resolveConditions = (
  eventProperties = {}, //NOSONAR
  action,
  formValues,
  conditions = [
    resolvePatternCondition,
    resolveEqualityCondition,
    resolvePresenceCondition,
  ]
) =>
  (function recurse(props) {
    //NOSONAR
    if (props.oneOf) {
      if (isArray(props.oneOf)) {
        for (const subEventProperties of props.oneOf) {
          const subEvent = recurse(subEventProperties);

          if (subEvent.properties) {
            return subEvent;
          }
        }
      }

      return {};
    }

    if (props.if) {
      return conditions.every((condition) =>
        condition(props.if, action, formValues)
      )
        ? props.then || {}
        : props.else || {};
    }

    return props;
  })(eventProperties);

export const combineProperties = (eventProperties) => {
  return {
    ...defaultProperties,
    ...eventProperties,
  };
};

export const interpolateProperties = (combinedProperties, options) => {
  const properties = JSON.stringify(combinedProperties);

  const interpolatedProperties = template(properties, {
    imports: {
      _: { get },
    },
  })({ config, ...options });

  return JSON.parse(interpolatedProperties);
};

export const getPropertiesAtKeyPath = (action, properties) => {
  const transformedKeyPath = transformKeyPath(action.keyPath);

  const eventPropertiesAtKeyPath = get(
    properties,
    removeIndexFromKeyPath(transformedKeyPath),
    null
  );

  if (eventPropertiesAtKeyPath) {
    const formValuesAtKeyPath = get(
      action.values,
      values(transformedKeyPath),
      null
    );

    if (
      eventPropertiesAtKeyPath.properties ||
      eventPropertiesAtKeyPath.oneOf ||
      eventPropertiesAtKeyPath.if
    ) {
      const eventProperties = resolveConditions(
        eventPropertiesAtKeyPath,
        action,
        formValuesAtKeyPath
      );

      if (eventProperties.properties) {
        const combinedProperties = combineProperties(
          eventProperties.properties
        );

        return {
          formValuesAtKeyPath,
          eventPropertiesAtKeyPath: interpolateProperties(combinedProperties, {
            action: {
              ...action,
              keyPath: transformedKeyPath,
            },
            value: formValuesAtKeyPath,
          }),
        };
      }

      return {};
    }

    return { formValuesAtKeyPath };
  }

  return {};
};
